import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormToggle from '../../../components/form/FormToggle';
import FormTextarea from '../../../components/form/FormTextarea';
import FormNumberInput from '../../../components/form/FormNumberInput';

import {
  whatsappMetaAdd,
  whatsappMetaUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';

const AddEditWhatsappMetaModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const initialValues = {
    phone_number_id: '',
    whatsapp_number: '',
    wba_id: '',
    whatsapp_token: '',
    webhook_verify_token: '',
    is_bot_number: 'no'
  };

  const validationSchema = Yup.object({
    phone_number_id: Yup.string().required('phone number id is required'),
    whatsapp_number: Yup.string().required('whatsapp number is required'),
    wba_id: Yup.string().required('whatsApp business account id is required'),
    whatsapp_token: Yup.string().required('whatsapp token is required'),
    webhook_verify_token: Yup.string().required('webhook verify token is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(whatsappMetaUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const response = await dispatch(whatsappMetaAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...values,
        phone_number_id: editRecord?.phone_number_id || '',
        whatsapp_number: editRecord?.whatsapp_number || '',
        wba_id: editRecord?.wba_id || '',
        whatsapp_token: editRecord?.whatsapp_token || '',
        webhook_verify_token: editRecord?.webhook_verify_token || '',
        is_bot_number: editRecord?.is_bot_number || 'no'
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Whatsapp Meta' : 'Add Whatsapp Meta'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="phone_number_id"
                label="Phone Number ID"
                placeholder="Enter Phone Number ID"
                value={values.phone_number_id}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormNumberInput
                name="whatsapp_number"
                label="Whatsapp Number"
                placeholder="Enter Whatsapp Number"
                value={values.whatsapp_number}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\D/g, '');
                  const numericValue = rawValue === '' ? '' : parseInt(rawValue, 10);
                  setFieldValue('whatsapp_number', numericValue);
                }}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === '.') {
                    e.preventDefault();
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                pattern="^[0-9]*$"
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="wba_id"
                label="WhatsApp Business Account ID"
                placeholder="Enter WhatsApp Business Account ID"
                value={values.wba_id}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="webhook_verify_token"
                label="Webhook Verify Token"
                placeholder="Enter Webhook Verify Token"
                value={values.webhook_verify_token}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="9">
              <FormTextarea
                name="whatsapp_token"
                label="Whatsapp Token"
                placeholder="Enter Whatsapp Token"
                value={values.whatsapp_token}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_bot_number"
                trueName="Yes"
                falseName="No"
                label="Bot Number"
                value={String(values.is_bot_number === 'yes')}
                onChange={(e) =>
                  setFieldValue('is_bot_number', values.is_bot_number === 'yes' ? 'no' : 'yes')
                }
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditWhatsappMetaModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditWhatsappMetaModal;
